import logger from '@/lib/logger';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MeasurementWizardHeader } from '@/features/measurement/components/MeasurementWizardHeader';
import { StepProgressIndicator } from './components/StepProgressIndicator';
import { CancelConfirmationDialog } from './components/CancelConfirmationDialog';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useDeviceService } from '@/contexts/DeviceServiceContext';
import { ElectrodeDetect } from '@/lib/protobuf';
import type { DeviceState } from '@/types/device';
import { profileService } from '@/services/api/profileService';
import { electrodeTypeService } from '@/services/api/electrodeTypeService';
import { testService } from '@/services/api/testService';
import { Step1TestPanel } from '@/features/measurement/components/steps/Step1TestPanel';
import { Step2ApplySolution } from '@/features/measurement/components/steps/Step2ApplySolution';
import { Step3TakeSpecimen } from '@/features/measurement/components/steps/Step3TakeSpecimen';
import { Step4StirSwab } from '@/features/measurement/components/steps/Step4StirSwab';
import { Step5ApplySpecimen } from '@/features/measurement/components/steps/Step5ApplySpecimen';
import { StepMeasurementPhase } from '@/features/measurement/components/steps/StepMeasurementPhase';
import { useBeforeUnloadGuard } from '@/features/measurement/hooks/useBeforeUnloadGuard';
import { useSpecimenMeasurement } from '@/features/measurement/hooks/useSpecimenMeasurement';
import { isUnknownDeviceUid } from '@/services/device/constants';
import { WizardDeviceDisconnectedDialog } from './components/WizardDeviceDisconnectedDialog';
import { DeviceReconnectDialog } from '@/features/measurement/components/DeviceReconnectDialog';
import { MeasurementInterruptedModal } from '@/features/measurement/components/MeasurementInterruptedModal';
import './styles/MeasurementWizard.module.css';

interface MeasurementWizardProps {
  onCancel: () => void;
  mockMode?: boolean;
}

/**
 * MeasurementWizard - Full-screen wizard shell for the complete measurement process
 *
 * Steps 1–5: setup (select panel, apply reagent, baseline measurement, etc.)
 * Steps 6–7: incubation + specimen measuring (formerly ProgressResultsView)
 *
 * On measurement completion, navigates to /measurement/results/:testId.
 */
export function MeasurementWizard({ onCancel, mockMode = false }: MeasurementWizardProps) {
  const navigate = useNavigate();
  const deviceService = useDeviceService();
  const currentStep = useMeasurementWizardStore((s) => s.currentStep);
  const testId = useMeasurementWizardStore((s) => s.testId);
  const baselineCompletedAt = useMeasurementWizardStore((s) => s.baselineCompletedAt);
  const resetWizard = useMeasurementWizardStore((s) => s.resetWizard);
  const clearMeasurementInFlight = useMeasurementWizardStore((s) => s.clearMeasurementInFlight);
  const setInitialDeviceSnapshot = useMeasurementWizardStore((s) => s.setInitialDeviceSnapshot);
  const deviceInfo = useDeviceStore((s) => s.deviceInfo);
  const connectionState = useDeviceStore((s) => s.connectionState);
  const measurementInFlight = useMeasurementWizardStore((s) => s.measurementInFlight);

  // Specimen measurement orchestration — only active for steps 6-7.
  // mode='view' makes startMeasurement() a no-op during setup steps 1-5.
  const measurementActive = currentStep >= 6;
  const {
    phase,
    isConnected,
    measurementProgress,
    disconnectedMidMeasure,
    measureReconnecting,
    measureReconnectError,
    handleMeasureDisconnectReconnect,
    handleMeasureDisconnectCancel,
    showRefreshRecovery, setShowRefreshRecovery,
    reconnecting,
    reconnectError,
    incubationProgress,
    handleIncubationReconnect,
    handleInterruptedResume,
    handleInterruptedDiscard,
  } = useSpecimenMeasurement({
    testId,
    mode: measurementActive ? 'measurement' : 'view',
    onClose: onCancel,
  });

  // Prevent accidental page refresh once a measurement is in flight OR the baseline
  // has already completed (Steps 4–5).
  useBeforeUnloadGuard(measurementInFlight !== null || currentStep >= 4);

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [deviceBusyOnEntry, setDeviceBusyOnEntry] = useState(false);
  // Pre-measurement reconnect state (steps 1–5 only; measurement phases use hook's reconnect state)
  const [preReconnecting, setPreReconnecting] = useState(false);
  const [preReconnectError, setPreReconnectError] = useState<string | null>(null);
  const expectedDeviceUidRef = useRef<string | null>(null);
  useEffect(() => {
    if (deviceInfo?.uid && !expectedDeviceUidRef.current) {
      expectedDeviceUidRef.current = deviceInfo.uid;
    }
  }, [deviceInfo]);

  // Show blocking disconnect dialog when device unplugs during wizard steps 1–5.
  // Excludes 'specimen', 'incubating', and 'baseline-complete' phases, and steps 6-7 (which have
  // their own measurement-phase dialogs).
  const showPreMeasurementDisconnect =
    !mockMode &&
    (connectionState === 'disconnected' || connectionState === 'error') &&
    measurementInFlight?.phase !== 'specimen' &&
    measurementInFlight?.phase !== 'incubating' &&
    measurementInFlight?.phase !== 'baseline-complete' &&
    currentStep < 6;

  // Silent auto-reconnect as soon as the pre-measurement disconnect dialog becomes visible.
  useEffect(() => {
    if (showPreMeasurementDisconnect && !preReconnecting) {
      handlePreMeasurementReconnect();
    }
  // handlePreMeasurementReconnect is stable; guard prevents looping while reconnecting.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreMeasurementDisconnect]);

  const isCompletingRef = useRef(false);

  // Phase → navigation: completed measurement transitions to ResultsView
  useEffect(() => {
    if (phase !== 'completed' || currentStep < 6) return;
    isCompletingRef.current = true;
    const params = new URLSearchParams({ from: '/' });
    navigate(`/measurement/results/${testId}?${params.toString()}`);
  }, [phase, currentStep, testId, navigate]);

  // Prevent body scroll while wizard is open
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow || 'auto';
    const originalBodyOverflow = document.body.style.overflow || 'auto';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
    };
  }, []);

  // Reset wizard state on unmount (only if cancelled, not on successful completion)
  useEffect(() => {
    return () => {
      if (isCompletingRef.current) {
        logger.debug('[MeasurementWizard] Unmounting after completion - preserving device connection and wizard state');
        return;
      }
      // Don't reset if a recovery is pending — React StrictMode double-invokes effects
      // and the cleanup would wipe measurementInFlight before recovery can act on it.
      if (useMeasurementWizardStore.getState().measurementInFlight !== null) {
        logger.debug('[MeasurementWizard] Unmounting with pending recovery — preserving wizard state');
        return;
      }
      logger.debug('[MeasurementWizard] Unmounting - resetting wizard state');
      resetWizard();
    };
  }, [resetWizard]);

  // Stale-state guard: if the store has currentStep >= 6 but no measurementInFlight,
  // the previous run completed and the store was not cleaned up (navigation preserved it
  // for ResultsView). Reset so a fresh wizard run starts from step 1.
  useEffect(() => {
    const { measurementInFlight: inFlight, currentStep: step } = useMeasurementWizardStore.getState();
    if (inFlight === null && step >= 6) {
      logger.debug('[MeasurementWizard] Stale completed state detected — resetting store for fresh run');
      useMeasurementWizardStore.getState().resetWizard();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload recovery: if user reloaded while between baseline completion and specimen start
  // (Steps 4–5), measurementInFlight.phase === 'baseline-complete'.
  // Also handles 'incubating' (Step 6): incubation timer was running but device was idle.
  useEffect(() => {
    const { measurementInFlight: inFlight, currentStep: step } = useMeasurementWizardStore.getState();
    if (inFlight?.phase === 'baseline-complete' && step < 4) {
      logger.warn('[MeasurementWizard] Rehydrated with baseline-complete at step < 4 — restoring to Step 4');
      useMeasurementWizardStore.getState().setCurrentStep(4);
    }
    if (inFlight?.phase === 'incubating' && step < 6) {
      logger.warn('[MeasurementWizard] Rehydrated with incubating at step < 6 — restoring to Step 6');
      useMeasurementWizardStore.getState().setCurrentStep(6);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-connect mock device in mock mode
  useEffect(() => {
    if (mockMode) {
      deviceService.connect().then(() => {
        logger.debug('[MeasurementWizard] Mock device auto-connected');
      }).catch(err => {
        logger.error('[MeasurementWizard] Mock device connection failed:', err);
      });
    }
  }, [mockMode, deviceService]);

  // Ping device on startup to capture initial snapshot (both real and mock mode)
  useEffect(() => {
    const pingDevice = async () => {
      try {
        if (!deviceInfo?.uid) return;
        logger.debug('[MeasurementWizard] Pinging device to capture initial snapshot...');
        let response = await deviceService.getState();
        if (!response) {
          logger.warn('[MeasurementWizard] Failed to get device state');
          return;
        }
        if (!response.state) {
          logger.warn('[MeasurementWizard] Device did not include state in response. Retrying...');
          const retryResponse = await deviceService.getState();
          if (retryResponse && retryResponse.state) {
            logger.debug('[MeasurementWizard] Device state retrieved on retry');
            response = retryResponse;
          } else {
            logger.error('[MeasurementWizard] Device still not returning state after retry. Proceeding with default snapshot.');
          }
        }
        const snapshot = {
          busy: (response.state?.processingId ?? 0n) !== 0n,
          progress: response.state?.progressPct ?? 0,
          electrodeDetected: response.state?.electrode !== ElectrodeDetect.NC,
          // v1.4.4's ElectrodeDetect.CODE_1–CODE_7 have no known physical electrode
          // mapping yet (blocked on hardware/EE) — surface an explicit unknown state
          // rather than guessing a channel-count label, consistent with
          // DeviceService.ts's updateDeviceState() mapping.
          electrodeType: 'unknown' as DeviceState['electrodeType'],
          temperature: response.state?.temperatureC ?? 0,
          humidity: response.state?.humidityPct ?? 0,
          batteryLevel: response.state?.batteryPct ?? 0,
          batteryVoltage: response.state?.batteryV ?? 0,
          powerState: (response.state?.powerState?.toString() ?? 'unknown') as any,
          warnings: Number(response.state?.issueBitmask ?? 0n),
          pointsMax: response.limits?.pointsMax ?? null,
        };
        setInitialDeviceSnapshot(snapshot);
        logger.debug('[MeasurementWizard] Initial device snapshot captured:', snapshot);
        const inFlight = useMeasurementWizardStore.getState().measurementInFlight;
        if (snapshot.busy && !inFlight) {
          logger.warn('[MeasurementWizard] Device is busy — cannot start wizard');
          setDeviceBusyOnEntry(true);
        }
      } catch (err) {
        logger.error('[MeasurementWizard] Error pinging device:', err);
      }
    };
    void pingDevice();
  }, [deviceInfo, deviceService, setInitialDeviceSnapshot]);

  // Initialize default profile and electrode type on mount.
  // Skip if the wizard has persisted state (recovery scenario).
  useEffect(() => {
    const { testId: persistedTestId, currentStep: persistedStep } = useMeasurementWizardStore.getState();
    if (persistedTestId && persistedStep > 1) {
      logger.debug('[MeasurementWizard] Resuming from persisted state — skipping default init');
      return;
    }
    const initializeDefaults = async () => {
      try {
        logger.debug('[MeasurementWizard] Loading defaults...');
        const defaultProfile = await profileService.getDefaultProfile();
        useMeasurementWizardStore.getState().setSelectedProfile(defaultProfile);
        logger.debug('[MeasurementWizard] Default profile loaded:', defaultProfile.name);
        const electrodeTypes = await electrodeTypeService.getElectrodeTypes();
        if (electrodeTypes.length > 0) {
          const defaultElectrode = electrodeTypes.find(et => et.channelCount === 4) || electrodeTypes[0];
          useMeasurementWizardStore.getState().setSelectedElectrodeType(defaultElectrode);
          logger.debug('[MeasurementWizard] Default electrode type loaded:', defaultElectrode.name);
        } else {
          logger.warn('[MeasurementWizard] No electrode types available');
        }
      } catch (error) {
        logger.error('[MeasurementWizard] Failed to load defaults:', error);
      }
    };
    initializeDefaults();
  }, []);

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = async () => {
    setShowCancelDialog(false);
    const isMeasuring = useDeviceStore.getState().measurementProgress.isRunning;
    if (isMeasuring) {
      try {
        await deviceService.cancelMeasurement();
        logger.debug('[MeasurementWizard] ABORT sent to device on cancel');
      } catch (error) {
        logger.error('[MeasurementWizard] Failed to send ABORT to device:', error);
      }
    }
    const currentTestId = useMeasurementWizardStore.getState().testId;
    if (currentTestId) {
      try {
        await testService.cancelTest(currentTestId);
        logger.debug('[MeasurementWizard] Test cancelled on backend:', currentTestId);
      } catch (error) {
        logger.error('[MeasurementWizard] Failed to cancel test on backend:', error);
      }
    }
    clearMeasurementInFlight();
    onCancel();
    navigate('/');
  };

  const handleCancelDismiss = () => {
    setShowCancelDialog(false);
  };

  const handlePreMeasurementReconnect = async () => {
    setPreReconnecting(true);
    setPreReconnectError(null);
    try {
      const success = await deviceService.tryAutoReconnect();
      if (!success) {
        setPreReconnectError('Could not reconnect automatically. Try unplugging and reconnecting the USB cable.');
        return;
      }
      const reconnectedUid = useDeviceStore.getState().deviceInfo?.uid;
      if (
        !isUnknownDeviceUid(reconnectedUid) &&
        !isUnknownDeviceUid(expectedDeviceUidRef.current) &&
        reconnectedUid !== expectedDeviceUidRef.current
      ) {
        logger.warn('[MeasurementWizard] UID mismatch on reconnect — disconnecting wrong reader');
        await deviceService.disconnect();
        setPreReconnectError('The connected reader does not match the one used to start this test. Please reconnect the original reader.');
        return;
      }
      const inFlight = useMeasurementWizardStore.getState().measurementInFlight;
      const stepNow = useMeasurementWizardStore.getState().currentStep;
      if (inFlight?.phase === 'baseline' && stepNow >= 4) {
        logger.debug('[MeasurementWizard] Baseline was in-flight during disconnect — returning to Step 3 for recovery');
        useMeasurementWizardStore.getState().setCurrentStep(3);
      }
    } catch (err) {
      logger.error('[MeasurementWizard] Pre-measurement reconnect failed:', err);
      setPreReconnectError('Could not reconnect. Please try again.');
    } finally {
      setPreReconnecting(false);
    }
  };

  return (
    <Box className="measurement-wizard" sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <MeasurementWizardHeader
        showCancelButton={true}
        showDeviceStatus={true}
        onCancel={handleCancelClick}
      />

      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: '100vw',
        minHeight: 0,
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: 1600,
          px: { xs: 2, md: 4, lg: 6 },
          py: 3,
        }}>
          {currentStep <= 5 && (
            <StepProgressIndicator
              currentStep={currentStep}
              totalSteps={5}
            />
          )}

          <Box>
            {currentStep === 1 && <Step1TestPanel />}
            {currentStep === 2 && <Step2ApplySolution />}
            {currentStep === 3 && <Step3TakeSpecimen />}
            {currentStep === 4 && <Step4StirSwab />}
            {currentStep === 5 && <Step5ApplySpecimen />}
            {currentStep >= 6 && (phase === 'incubation' || phase === 'measuring') && (
              <StepMeasurementPhase
                phase={phase}
                incubationProgress={incubationProgress}
                measurementProgress={measurementProgress}
              />
            )}
          </Box>
        </Box>
      </Box>

      <CancelConfirmationDialog
        open={showCancelDialog}
        onCancel={handleCancelConfirm}
        onContinue={handleCancelDismiss}
        hasStartedBaseline={!!baselineCompletedAt}
      />

      {/* Pre-measurement disconnect dialog — steps 1–5 only */}
      <WizardDeviceDisconnectedDialog
        open={showPreMeasurementDisconnect}
        reconnecting={preReconnecting}
        reconnectError={preReconnectError}
        onReconnect={handlePreMeasurementReconnect}
        onCancelTest={handleCancelConfirm}
      />

      {/* Measurement-phase disconnect / recovery dialogs — steps 6–7 */}
      {currentStep >= 6 && (
        <>
          {/* Incubation disconnect — non-blocking (incubation timer continues) */}
          <DeviceReconnectDialog
            open={!isConnected && phase === 'incubation' && !showRefreshRecovery}
            message="The reader was disconnected during incubation. Reconnect it to continue — incubation progress is preserved."
            reconnecting={reconnecting}
            reconnectError={reconnectError}
            onReconnect={handleIncubationReconnect}
            onCancelTest={handleCancelConfirm}
          />

          {/* Refresh recovery — stale measurementInFlight detected on mount */}
          {showRefreshRecovery && (
            <MeasurementInterruptedModal
              phase="specimen"
              onResume={(outcome) => { setShowRefreshRecovery(false); handleInterruptedResume(outcome); }}
              onDiscard={() => { setShowRefreshRecovery(false); handleInterruptedDiscard(); }}
            />
          )}

          {/* Measuring disconnect — DeviceReconnectDialog with silent auto-recovery */}
          <DeviceReconnectDialog
            open={disconnectedMidMeasure && !showRefreshRecovery}
            message="The reader was disconnected during measurement. Reconnect it to continue — the measurement will resume automatically."
            reconnecting={measureReconnecting}
            reconnectError={measureReconnectError}
            onReconnect={handleMeasureDisconnectReconnect}
            onCancelTest={handleMeasureDisconnectCancel}
          />
        </>
      )}

      {/* Device-busy blocking dialog — shown when device was already running on wizard entry */}
      <Dialog
        open={deviceBusyOnEntry}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Device is Busy</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            The reader is currently running a measurement. Please wait for it to
            finish (or power-cycle the device) before starting a new test.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { onCancel(); navigate('/'); }}>
            Close Wizard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
